import Backbone from "backbone";
import rison from "rison";
import _ from "underscore";
import { isEldTestTransferEnabled, getEldProviderName, isEldTransferValidateOnly } from "../../../utils/helpers";
import * as logger from "../../../utils/logger";
import { getServerDate } from "../../../utils/storage/date";
import { getLastPosition } from "../../../utils/location";
import DriveView from "../../../DriveView";
import { handleApi } from "../../../utils/error";
import { extend, htmlEscape } from "../../../utils/vanilla";
import constants from "../../../constants";
import { makeDeviceCache, makeUserCache } from "../../../utils/storage/cache";
import { Device } from "../../../model/Device";
import { IEntity } from "../../../model/Entity";
import { InspectionType } from "../inspectionHelper";
import { PageLevel } from "../../../store/mobile/types";
import { BigSpinner } from "../../../utils/spinner";
import TransferDialog from "../transferDialog";
import { getCurrentVehicle } from "../../../utils/device";
import transferHTML from "./transfer.tmpl";
import { isInCanada, CanadaEldOperatingSystems } from "hos-tools";
import { isIphone } from "../../../utils/native/nativeHelpers";
import { isArgumentException, isInvalidOperationException, isNetworkError, isOverLimitException } from "../../../utils/errorHandler/error.types";
import { FeatureFlagName } from "../../../utils/featureFlagName";
import { RoadsideCheckLanguageSelector } from "../roadsideCheckLanguageSelector";

export enum EldTransferMode {
    CanadaEmail = "CanadaEmail",
    Email = "Email",
    WebServices = "WebServices",
    Ping = "Ping"
}

export enum EldResponseStatus {
    Success = "Success",
    Error = "Error",
    Warning = "Warning",
    Information = "Information",
    NotValidated = "NotValidated",
    InvalidRegistrationData = "InvalidRegistrationData",
    EldDecertified = "EldDecertified",
    Timeout = "Timeout"
}

export interface IEldResponse {
    broadcast?: string;
    status: EldResponseStatus;
    errorMessage?: string;
    isDelivered: boolean;
}

interface IWebServicesResponse extends IEldResponse {
    id: string;
    errors: IWebServicesError[];
}

interface IPingResponse extends IEldResponse {
    detail: string | undefined;
}

export interface IResponseUI {
    responseText: string;
    resultStatus: string;
    statusMessage: string;
}

type TValidationEntityType = "Vehicle" | "Driver";

interface IWebServicesError {
    message: string;
    detail: string;
    start: number;
    end: number;
    line: number;
    errorType: EldResponseStatus;
    validationEntityType?: TValidationEntityType;
    validationEntityId?: string;
}

class HosTransfer extends Backbone.Model {
    defaults() {
        return {
            sendToInspector: !isEldTestTransferEnabled(),
            result: "",
            email: "",
            comment: "",
            status: "",
            statusMessage: "",
            broadcast: "",
            responseText: "",
            errorMessage: "",
            isTransferring: false,
            isPromptOpen: false
        };
    }
}

export interface IMissingEldElements {
    vinMissing: boolean;
    engineHoursMissing: boolean;
    distanceMissing: boolean;
    powerUnitMissing: boolean;
    activeEngineSync: boolean;
}

export default class TransferView extends DriveView<Backbone.Model> {

    protected strings = [
        "Logs will be validated and transferred to FMCSA for {0} review only.",
        "Logs will be validated but will not transferred to FMCSA for {0} review.",
        "The logs will be emailed to the email address you entered. The contents of the email will meet requirements for {0}.",
        "Transfer ({0})",
        "Email transfer ({0})",
        "Canada",
        "USA",
        "I'm not in {0}",
        "USA ELD",
        "Canadian ELD"
    ];
    private readonly template;
    private readonly eldResponse;
    private readonly userCache;
    private readonly deviceCache;
    private language: string;
    private errorStatus;
    private isWarningStatus: boolean;
    private isCanadaEld: boolean;
    private readonly originalJurisdiction?: string;
    private currentCountry: string;
    private transferDialog: TransferDialog;
    private readonly spinner = new BigSpinner();
    private readonly languageSelector = new RoadsideCheckLanguageSelector({
        app: this.app,
        getTranslator: () => this.translator,
        getElement: () => this.el,
        getHosRuleSetIdShortened: () => this.app.hosUtils.getLatestHosRuleSetIdShortened(),
        onLanguageApplied: language => this.switchLanguage(language)
    });

    constructor (options) {
        super(options, {
            title: "Roadside Check",
            activeSection: "inspection",
            pageLevel: PageLevel.RETURN_DASHBOARD
        });
        this.eldResponse = new HosTransfer();
        this.deviceCache = makeDeviceCache(this.app.store);
        this.userCache = makeUserCache(this.app.store);
        this.template = _.template(transferHTML);
        this.language = this.app.store.getState().mobile.language || "en";
        this.errorStatus = "";

        /* We get the jurisdiction of the current user based on their HOS ruleset and set the variables for the transfer Eld
        that correspond to the user's jurisdiction. */
        let currentUser = this.app.userService.getCurrentUser();
        if (this.originalJurisdiction === undefined) {
            if (currentUser && isInCanada(this.app.hosUtils.getLatestHosRuleSetIdShortened())) {
                this.isCanadaEld = true;
                this.currentCountry = "Canada";
                this.originalJurisdiction = "Canada";
            } else {
                this.isCanadaEld = false;
                this.currentCountry = "USA";
                this.originalJurisdiction = "USA";
            }
        }
        this.listenTo(this.eldResponse, "change", this.render.bind(this));
    }

    formatError = (error: IWebServicesError) => {
        let entity;
        if (error.validationEntityId && error.validationEntityType) {
            let entities: IEntity[] = [];
            if (error.validationEntityType === "Vehicle") {
                entities = this.deviceCache.get({id: error.validationEntityId}) as Device[];
            } else if (error.validationEntityType === "Driver") {
                let user = this.app.userService.getUserById(error.validationEntityId);
                entities = user ? [user] : this.userCache.get({id: error.validationEntityId});
            }
            if (entities && entities.length > 0) {
                entity = entities[0];
            }
        }
        let entitySpan = entity && entity.name ? `<ul><li>${ error.validationEntityType }: ${ htmlEscape(entity.name) }</li></ul>` : "";
        return `<li class="eld-error-${ error.errorType.toLowerCase() }">${ error.message }. ${ error.detail } [${ error.errorType }]${ entitySpan }</li>`;
    };

    getResponseText(mode: EldTransferMode | undefined, response: IEldResponse, formatError: (error: IWebServicesError) => string): IResponseUI {
        let responseUI: IResponseUI = {responseText: "", resultStatus: "", statusMessage: ""};

        if (response.isDelivered) {
            responseUI.resultStatus = "Delivered";
            responseUI.statusMessage = "Your logs have been successfully sent to the Officer for review.";
            if (!this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_HIDING_FMCSA_ELD_TRANSFER_INFO) && response.status === EldResponseStatus.Warning) {
                responseUI.statusMessage += " Click the + symbol below to see the [Warning] message from the FMCSA.";
            }
            this.logComplianceEvent(logger.Event.COMPLIANCETRANSFER, `Transfer: ${ mode } success`);
        } else {
            responseUI.resultStatus = "Failed";
            responseUI.statusMessage = "The driver logs you requested were not sent because of the errors below. ";
            responseUI.statusMessage += "Correct these issues before sending a transfer." +
                                " If transfer is unavailable or fails, the driver can display the ELD records by clicking the Compliance print button.";
            this.logComplianceEvent(logger.Event.COMPLIANCETRANSFER, `Transfer: ${ mode } error`);
        }

        switch (mode) {
            case EldTransferMode.WebServices:
            case EldTransferMode.Email: {
                let wsResponse = <IWebServicesResponse>response;

                if (wsResponse.status !== EldResponseStatus.Success || wsResponse.errors.length > 0) {
                    if (response.status === EldResponseStatus.Timeout) {
                        responseUI.responseText += "Timeout communicating with FMCSA web service";
                    }

                    if (wsResponse.errors && wsResponse.errors.length > 0) {
                        responseUI.responseText += `<ul class="eld-error-list">${ wsResponse.errors.map(formatError).join("") }</ul>`;
                    }
                }
                break;
            }
            case EldTransferMode.Ping: {
                let pingResponse = <IPingResponse>response;
                responseUI.responseText += `${ pingResponse.detail || "" }`;
                break;
            }
            case EldTransferMode.CanadaEmail: {
                let wsResponse = <IWebServicesResponse>response;
                if (wsResponse && wsResponse.errors && wsResponse.errors.length > 0 && wsResponse.errors[0].message) {
                    responseUI.statusMessage = wsResponse.errors[0].message;
                }
                break;
            }
            default:
                break;
        }
        return responseUI;
    }

    getMissingEldElements() {
        let missingEldElements: IMissingEldElements = {
            vinMissing: false,
            engineHoursMissing: false,
            distanceMissing: false,
            powerUnitMissing: false,
            activeEngineSync: false
        };

        this.app.dutyStatusLogService.getHOSLogs().filter(log =>
            !log.isNoDevice() && (
            (!log.isAutomaticLog() && log.isNormalLog()) ||
            log.isLoginLogoffLog() ||
            log.status === "Certify" ||
            log.status === "ExemptionOffDutyDeferral" ||
            log.status === "CanadaCycleOne" ||
            log.status === "CanadaCycleTwo")).forEach(log => {
            let device;
            let seenDevices = {};
            if (!Object.keys(seenDevices).length) {
                device = this.deviceCache.get({ id: log.device.id});
                seenDevices[log.device.id] = true;
            } else {
                if (!(log.device.id in seenDevices)) {
                    device = this.deviceCache.get({ id: log.device.id});
                    seenDevices[log.device.id] = true;
                }
            }

            if (device && device.length) {
                if (!device[0].vehicleIdentificationNumber) {
                    missingEldElements.vinMissing = true;
                }

                if (!device[0].name) {
                    missingEldElements.powerUnitMissing = true;
                }
            }

            if (!log.engineHours) {
                missingEldElements.engineHoursMissing = true;
            }

            if (!log.odometer) {
                missingEldElements.distanceMissing = true;
            }
        });

        return missingEldElements;
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "keyup #transfer-log-email": this.updateEmail,
            "keyup #transfer-log-comment": this.updateComment,
            "click #transfer-log-webservices": this.webservices,
            "click #transfer-log-ping": this.ping,
            "click #complianceReport": this.showComplianceReport,
            "click #switch-from-USA-transfer": this.navigateToCanadaTransfer,
            "click #switch-from-Canada-transfer": this.navigateToUSATransfer,
            "click #transferCanada": this.transferCanadaEld,
            "click #transferUSA": this.transferUSAEld,
            "click #jurisdictionWarningCanada": this.navigateToCanadaTransfer,
            "click #jurisdictionWarningUSA": this.navigateToUSATransfer,
            "click .details-toggle": this.showTransferDetails,
            "click #roadside-check-language-selector": this.onLanguageSelectorClick
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render (scroll = true) {
        void Promise.all([
            this.translator.translate(this.strings, this.language),
            this.translator.translate(this.errorStatus, this.language)
        ])
        .then(([translatedStrings, translatedError]) => {
            let translatedCurrentCountry = translatedStrings[this.currentCountry === "Canada" ? 5 : 6];
            let translatedTransferText = translatedStrings[3].replace("{0}", translatedCurrentCountry);
            let translatedEmailTransferText = translatedStrings[4].replace("{0}", translatedCurrentCountry);
            let imNotInCountryText = translatedStrings[7].replace("{0}", translatedCurrentCountry);
            let viewObj = extend({
                debug: isEldTestTransferEnabled(),
                additionalError: translatedError && translatedError.length > 0 ? `<span class="noTranslate"> (${ translatedError })</span>` : "",
                isCanadaEld: this.isCanadaEld,
                currentCountry: this.currentCountry,
                originalJurisdiction: this.originalJurisdiction,
                email: this.eldResponse.get("email"),
                comment: this.eldResponse.get("comment"),
                translatedTransferText,
                translatedEmailTransferText,
                imNotInCountryText,
                ...this.languageSelector.getViewModel(),
                disableHidingFMCSAInfo: this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_HIDING_FMCSA_ELD_TRANSFER_INFO),
                isWarningStatus: this.isWarningStatus
            }, this.eldResponse.toJSON());

            if (this.isCanadaEld) {
                viewObj.canadaOnlyWarning = translatedStrings[2].replace("{0}", `<span class="eld-response-warn">${ translatedStrings[9] }</span>`);
            } else {
                viewObj.usaOnlyWarning = (viewObj.sendToInspector ? translatedStrings[0] : translatedStrings[1]).replace(
                    "{0}",
                    `<span class="eld-response-warn">${ translatedStrings[8] }</span>`
                );
            }
            return this.display(this.template(viewObj), this.language).then(() => {
                if (this.eldResponse.get("isTransferring")) {
                    this.spinner && this.spinner.stop();
                    this.spinner.spin(document.querySelector("#spinner-container"));
                } else {
                    this.spinner && this.spinner.stop();
                }

                let errorMessage = document.querySelector("#eld-response-error") as HTMLElement;
                if (errorMessage) {
                    let scrollToErrorMessageBtn = document.querySelector("#eld-response-error-scroll-btn") as HTMLElement;
                    if (scrollToErrorMessageBtn) {
                        scrollToErrorMessageBtn.onclick = () => {
                            errorMessage.scrollIntoView();
                        };
                    }
                }

                if (scroll) {
                    // scroll down to results if we have them
                    let resultTd = document.querySelector("td#eld-respond-result") as HTMLElement;

                    if (resultTd) {
                        resultTd.scrollIntoView();
                    }
                }
            });
        });

        return this;
    }

    webservices() {
        this.transfer(EldTransferMode.WebServices);
    }

    displayErrorMessage() {
        let resultDiv = document.querySelector("div#empty-email-response") as HTMLElement;

        if (resultDiv) {
            // makes email error block visible
            resultDiv.style.display = "block";

            // scrolls up to the error message for the missing email address
            resultDiv.scrollIntoView();
        }
    }

    ping() {
        this.transfer(EldTransferMode.Ping);
    }

    transferCanadaEld() {
        if (this.eldResponse.get("email") === "") {
            this.displayErrorMessage();
            return;
        }
        this.transfer(EldTransferMode.CanadaEmail);
    }

    transferUSAEld() {
        this.transfer(EldTransferMode.Email);
    }

    showComplianceReport() {
        this.logComplianceEvent(logger.Event.COMPLIANCEDISPLAY, `Generate`);

        if (this.isCanadaEld) {
            this.app.router.navigate(`inspection/main,${ rison.encode({ type: InspectionType.CANELD_HOS_REPORT }) }`, true);
        } else {
            this.app.router.navigate(`inspection/main,${ rison.encode({ type: InspectionType.HOS_REPORT }) }`, true);
        }
    }

    updateEmail(e) {
        this.eldResponse.set("email", e.currentTarget.value, { silent: true });
    }

    updateComment(e) {
        this.eldResponse.set("comment", e.currentTarget.value, { silent: true });
    }

    navigateToUSATransfer() {
        this.isCanadaEld = false;
        this.currentCountry = "USA";
        this.render(true);
    }

    navigateToCanadaTransfer() {
        this.isCanadaEld = true;
        this.currentCountry = "Canada";
        this.render(true);
    }

    switchLanguage(language: string): void {
        if (language !== this.language) {
            this.language = language;
            this.render(false);
        }
    }

    remove() {
        this.languageSelector.remove();
        return super.remove();
    }

    onLanguageSelectorClick() {
        this.languageSelector.handleClick();
    }

    showTransferDetails(event: Event) {
        event.preventDefault();
        if (event.target instanceof Element) {
            const container = event.target.closest(".collapsible-container");
            if (container) {
                container.classList.toggle("is-open");
            }
        }
    }

    /**
     * Initiates an async promise for getting the current position and logging it as an event
     */
    logComplianceEvent(eventType: logger.Event, message: string) {
        getLastPosition(this.app.store)
            .then(position => {
                logger.event(
                    eventType,
                    `${ message }; Location: ${ position.coords.latitude },${ position.coords.longitude }; Accuracy: ${ position.coords.accuracy }; Speed: ${ position.coords.speed };`
                );
            })
            .catch((error: GeolocationPositionError) => {
                logger.event(eventType, `${ message }; Location error: ${ error?.message }`);
            });
    }

    private isInEngineSyncMalfunction(device: IDevice) {
        if (!this.app.store.getState().mobile.eldMalfunction) {
            return false;
        }

        let latestLog = this.app.dutyStatusLogService.getLatestLog(
            this.app.dutyStatusLogService.getHOSLogs(true, undefined, false, true, true),
            undefined,
            log => log.status === "EngineSyncCompliance" && log.device.id === device.id
        );

        return latestLog ? latestLog.malfunction === "Malfunction" : false;
    }

    private callTransferEld(currentUser: DeepReadonly<IUser>, fromDate: Date, database: string, mode: EldTransferMode) {
        if (!this.eldResponse.get("isPromptOpen")) {
            this.app.api.call("TransferEld", {
                driverId: currentUser.id,
                fromDate: fromDate.toISOString(),
                toDate: getServerDate().toISOString(),
                transferMode: mode,
                comment: this.eldResponse.get("comment"),
                providerName: getEldProviderName(database, window.whitelabel, mode === EldTransferMode.CanadaEmail),
                coordinates: null,
                isTest: !this.eldResponse.get("sendToInspector"),
                validateOnly: isEldTransferValidateOnly(),
                recipientEmailAddress: this.eldResponse.get("email"),
                encrypt: true,
                includeCSV: false,
                os: isIphone() ? CanadaEldOperatingSystems.IOS : CanadaEldOperatingSystems.Android,
                serverUrl: geotab.location.host
            }, apiResult => {
                this.handleResponse(apiResult, mode);
            }, error => {
                let errorMessage = "";

                if (isInvalidOperationException(error) || isArgumentException(error) || isOverLimitException(error) || isNetworkError(error)) {
                    errorMessage = error.message;
                } else {
                    handleApi(error);
                }

                this.handleResponse({
                    status: EldResponseStatus.Error,
                    errorMessage: errorMessage,
                    isDelivered: false
                }, mode);
            });
        }
    }

    private transfer(mode: EldTransferMode) {
        let currentUser = this.app.userService.getCurrentUser();
        let fromDate = this.app.hosUtils.getRetentionStartDate();

        if (!currentUser) {
            throw new Error("No current user");
        }

        // Clear previous results and trigger render
        this.eldResponse.set({
            result: "",
            status: "",
            statusMessage: "",
            broadcast: "",
            responseText: "",
            errorMessage: "",
            isTransferring: true,
            isPromptOpen: false
        });

        let credentials = this.app.api.getCurrentCredentials();
        let database = credentials ? credentials.credentials.database : "";

        let missingEldElements = this.getMissingEldElements();

        // Get current device and check for active engine sync
        let device = this.deviceCache.get({ id: getCurrentVehicle()?.id });
        if (device && device.length && this.isInEngineSyncMalfunction(device[0] as IDevice)) {
            missingEldElements.activeEngineSync = true;
        }

        // Check that the user is in Canada ELD transfer and if one of the ELD elements are missing from the logs
        if (this.currentCountry === "Canada" && (missingEldElements.distanceMissing || missingEldElements.engineHoursMissing ||
            missingEldElements.powerUnitMissing || missingEldElements.vinMissing || missingEldElements.activeEngineSync)) {
            // If missing and in Canada ELD transfer then we show the prompt
            this.eldResponse.set("isPromptOpen", true);
            this.transferDialog = new TransferDialog({ translator: this.translator, isUserNightMode: this.app.store.getState().data.nightMode });
            this.transferDialog.open(missingEldElements).then((choice) => {
                // Need to make sure that the transfer state for the spinner is off once the user makes a choice between cancel and proceed buttons
                this.eldResponse.set("isTransferring", false);
                this.eldResponse.set("isPromptOpen", false);
                this.transferDialog.close();
                // Do nothing if the user cancels
                if (!choice) {
                    return;
                }
                // If the user proceeds then continue calling transfer
                if (currentUser) {
                    this.callTransferEld(currentUser, fromDate, database, mode);
                }
            }).catch(handleApi);
        } else {
            this.callTransferEld(currentUser, fromDate, database, mode);
        }
    }

    private handleResponse(response: IEldResponse, mode?: EldTransferMode) {
        let formatStatus = (status: EldResponseStatus) => {
            switch (status) {
                case EldResponseStatus.Success:
                case EldResponseStatus.Error:
                    return "";
                default:
                    return status.split(constants.CAMELCASE_SEPARATOR_REGEXP).join(constants.SPACE_SEPARATOR);
            }
        };
        // If not success or error, show the error type beside the failure status
        this.errorStatus = formatStatus(response.status || EldResponseStatus.Error);
        this.isWarningStatus = response.status === EldResponseStatus.Warning;

        let responseUI = this.getResponseText(mode, response, this.formatError);
        this.eldResponse.set({
            status: responseUI.resultStatus,
            statusMessage: responseUI.statusMessage,
            broadcast: response.broadcast,
            responseText: responseUI.responseText,
            errorMessage: response.errorMessage,
            isTransferring: false,
            isPromptOpen: false
        });
    }
}
